import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DataTable } from '@/components/DataTable';
import { 
  Calendar, Clock, AlertTriangle, CheckCircle2, PlayCircle, 
  Search, RefreshCw, Wrench, Building2, Filter, UserPlus, ExternalLink
} from 'lucide-react';
import { format, parseISO, isAfter, isBefore, isToday, addDays } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_COLORS = {
  'Low': 'bg-gray-100 text-gray-600',
  'Medium': 'bg-blue-100 text-blue-700',
  'High': 'bg-orange-100 text-orange-700',
  'Critical': 'bg-red-100 text-red-700',
};

const STATUS_COLORS = {
  'Active': 'bg-green-100 text-green-700',
  'Paused': 'bg-yellow-100 text-yellow-700',
  'Complete': 'bg-blue-100 text-blue-700',
  'Archived': 'bg-gray-100 text-gray-600',
};

export default function PPMSchedulePage() {
  const [assigned, setAssigned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dueFilter, setDueFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showRunScheduler, setShowRunScheduler] = useState(false);
  const [schedulerResult, setSchedulerResult] = useState(null);
  const [schedulerRunning, setSchedulerRunning] = useState(false);
  
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('status', 'Active');
      params.append('limit', '500');
      if (dueFilter !== 'all') {
        params.append('due_filter', dueFilter);
      }
      if (priorityFilter !== 'all') {
        params.append('priority', priorityFilter);
      }
      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      
      const res = await axios.get(`${API}/api/ppm-core/assigned?${params.toString()}`, { headers });
      setAssigned(res.data.assigned || []);
    } catch (err) {
      console.error('Load error:', err);
      toast.error('Failed to load PPM schedule');
    } finally {
      setLoading(false);
    }
  }, [dueFilter, priorityFilter, searchTerm]);
  
  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadData();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const runScheduler = async () => {
    setSchedulerRunning(true);
    try {
      const res = await axios.post(`${API}/api/ppm-core/scheduler/run`, {}, { headers });
      setSchedulerResult(res.data);
      toast.success(`Created ${res.data.jobs_created} jobs (${res.data.skipped} skipped)`);
      loadData();
    } catch (err) {
      toast.error('Failed to run scheduler');
    } finally {
      setSchedulerRunning(false);
    }
  };

  const getDueStatus = (nextDueDate) => {
    if (!nextDueDate) return { label: 'Unknown', color: 'bg-gray-100 text-gray-600' };
    
    const dueDate = parseISO(nextDueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (isBefore(dueDate, today)) {
      return { label: 'Overdue', color: 'bg-red-100 text-red-700' };
    } else if (isToday(dueDate)) {
      return { label: 'Due Today', color: 'bg-orange-100 text-orange-700' };
    } else if (isBefore(dueDate, addDays(today, 7))) {
      return { label: 'Due Soon', color: 'bg-yellow-100 text-yellow-700' };
    }
    return { label: 'Scheduled', color: 'bg-green-100 text-green-700' };
  };

  const filteredAssigned = assigned.filter(a => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (a.task_name_snapshot || '').toLowerCase().includes(search) ||
      (a.asset?.park_name || '').toLowerCase().includes(search) ||
      (a.asset?.address || '').toLowerCase().includes(search)
    );
  });

  const columns = [
    {
      accessorKey: 'asset',
      header: 'Asset / Location',
      cell: ({ row }) => {
        const asset = row.original.asset || {};
        return (
          <div>
            <div className="font-medium">{asset.park_name || 'Unknown Park'}</div>
            <div className="text-xs text-slate-500">
              {asset.address} {asset.pitch_or_unit && `• Unit ${asset.pitch_or_unit}`}
            </div>
          </div>
        );
      }
    },
    {
      accessorKey: 'task_name_snapshot',
      header: 'PPM Task',
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.task_name_snapshot}</div>
          {row.original.is_legal_requirement && (
            <Badge className="bg-red-100 text-red-700 text-xs mt-1">Legal</Badge>
          )}
        </div>
      )
    },
    {
      accessorKey: 'next_due_date',
      header: 'Next Due',
      cell: ({ row }) => {
        const dueStatus = getDueStatus(row.original.next_due_date);
        return (
          <div>
            <div className="font-medium">
              {row.original.next_due_date ? format(parseISO(row.original.next_due_date), 'dd MMM yyyy') : 'Not set'}
            </div>
            <Badge className={`${dueStatus.color} text-xs mt-1`}>{dueStatus.label}</Badge>
          </div>
        );
      }
    },
    {
      accessorKey: 'frequency_months',
      header: 'Frequency',
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm">
          <Calendar className="h-3 w-3 text-slate-400" />
          Every {row.original.frequency_months} months
        </div>
      )
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      cell: ({ row }) => (
        <Badge className={PRIORITY_COLORS[row.original.priority] || 'bg-gray-100'}>
          {row.original.priority}
        </Badge>
      )
    },
    {
      accessorKey: 'last_completed_date',
      header: 'Last Completed',
      cell: ({ row }) => (
        <div className="text-sm text-slate-500">
          {row.original.last_completed_date 
            ? format(parseISO(row.original.last_completed_date), 'dd MMM yyyy')
            : 'Never'}
        </div>
      )
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge className={STATUS_COLORS[row.original.status] || 'bg-gray-100'}>
          {row.original.status}
        </Badge>
      )
    },
  ];

  // Calculate stats
  const stats = {
    total: assigned.length,
    overdue: assigned.filter(a => getDueStatus(a.next_due_date).label === 'Overdue').length,
    dueToday: assigned.filter(a => getDueStatus(a.next_due_date).label === 'Due Today').length,
    dueSoon: assigned.filter(a => getDueStatus(a.next_due_date).label === 'Due Soon').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-[#1F7A78]" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
      {/* Header - Mobile Friendly */}
      <div className="space-y-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800">PPM Live Schedule</h1>
          <p className="text-sm text-slate-500 mt-1">Active PPM assignments and due dates across all assets</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} size="sm" className="flex-1 sm:flex-none">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowRunScheduler(true)} size="sm" className="flex-1 sm:flex-none bg-[#1F7A78] hover:bg-[#16615f]">
            <PlayCircle className="h-4 w-4 mr-2" />
            Run Scheduler
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500 cursor-pointer hover:shadow-md" onClick={() => setDueFilter('all')}>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-xs text-slate-500">Total Active</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500 cursor-pointer hover:shadow-md" onClick={() => setDueFilter('overdue')}>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-600">{stats.overdue}</div>
            <div className="text-xs text-slate-500">Overdue</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-orange-500 cursor-pointer hover:shadow-md" onClick={() => setDueFilter('due_today')}>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-orange-600">{stats.dueToday}</div>
            <div className="text-xs text-slate-500">Due Today</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-yellow-500 cursor-pointer hover:shadow-md" onClick={() => setDueFilter('due_this_week')}>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.dueSoon}</div>
            <div className="text-xs text-slate-500">Due This Week</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters - Mobile Friendly */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input 
            placeholder="Search by task, park, or address..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="schedule-search-input"
          />
        </div>
        <div className="flex flex-wrap gap-2">
        <Select value={dueFilter} onValueChange={setDueFilter}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="due-filter">
            <SelectValue placeholder="Due filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Due Dates</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="due_today">Due Today</SelectItem>
            <SelectItem value="due_this_week">Due This Week</SelectItem>
            <SelectItem value="due_this_month">Due This Month</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-full sm:w-[150px]" data-testid="priority-filter">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="Critical">Critical</SelectItem>
            <SelectItem value="High">High</SelectItem>
            <SelectItem value="Medium">Medium</SelectItem>
            <SelectItem value="Low">Low</SelectItem>
          </SelectContent>
        </Select>
        </div>
      </div>

      {/* Mobile Cards View */}
      <div className="block md:hidden space-y-3">
        {filteredAssigned.length > 0 ? (
          filteredAssigned.map((item) => {
            const dueStatus = getDueStatus(item.next_due_date);
            const asset = item.asset || {};
            const hasLinkedJob = !!item.linked_job_id;
            const parkName = item.park_name || asset.park_name || 'Unknown Park';
            const address = item.address || asset.address || '';
            const pitchUnit = item.pitch_or_unit || asset.pitch_or_unit || '';
            
            return (
              <Card 
                key={item.id} 
                className={`border-l-4 cursor-pointer hover:shadow-md transition-shadow ${hasLinkedJob ? 'hover:bg-slate-50' : ''}`}
                style={{ borderLeftColor: dueStatus.label === 'Overdue' ? '#ef4444' : dueStatus.label === 'Due Today' ? '#f97316' : '#22c55e' }}
                onClick={() => {
                  if (hasLinkedJob) {
                    window.location.href = `/fm/jobs/${item.linked_job_id}`;
                  }
                }}
                data-testid={`ppm-card-${item.id}`}
              >
                <CardContent className="p-4">
                  <div className="space-y-2">
                    {/* Location */}
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-sm">{parkName}</p>
                        <p className="text-xs text-slate-500">{address} {pitchUnit && `• Unit ${pitchUnit}`}</p>
                      </div>
                      <Badge className={dueStatus.color}>{dueStatus.label}</Badge>
                    </div>
                    
                    {/* Task & Short Description */}
                    <div className="flex justify-between items-center pt-2 border-t">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.task_name_snapshot}</p>
                        <p className="text-xs text-slate-500 line-clamp-1">{item.description_snapshot}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {item.is_legal_requirement && (
                            <Badge className="bg-red-100 text-red-700 text-xs">Legal</Badge>
                          )}
                          <Badge className={PRIORITY_COLORS[item.priority] || 'bg-gray-100'} variant="outline">{item.priority}</Badge>
                        </div>
                      </div>
                      <div className="text-right ml-2">
                        <p className="text-xs text-slate-500">Next Due</p>
                        <p className="font-medium text-sm">
                          {item.next_due_date ? format(parseISO(item.next_due_date), 'dd MMM yyyy') : 'Not set'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Contractor & Schedule */}
                    {(item.assigned_contractor_name || item.linked_job_contractor) && (
                      <div className="flex items-center gap-2 text-xs pt-2 border-t">
                        <UserPlus className="h-3 w-3 text-slate-400" />
                        <span className="text-slate-600">{item.assigned_contractor_name || item.linked_job_contractor}</span>
                        {item.linked_job_scheduled_date && (
                          <>
                            <Calendar className="h-3 w-3 text-slate-400 ml-2" />
                            <span>{format(parseISO(item.linked_job_scheduled_date), 'dd MMM')}</span>
                          </>
                        )}
                      </div>
                    )}
                    
                    {/* Linked Job Status */}
                    <div className="flex justify-between items-center pt-2 border-t text-xs">
                      <div>
                        <span className="text-slate-500">Last Completed: </span>
                        <span className="font-medium">
                          {item.last_completed_date ? format(parseISO(item.last_completed_date), 'dd MMM yyyy') : 'Never'}
                        </span>
                      </div>
                      {hasLinkedJob ? (
                        <Badge className="bg-blue-100 text-blue-700 flex items-center gap-1">
                          <ExternalLink className="h-3 w-3" />
                          {item.linked_job_status || 'Job Linked'}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-500">No Job</Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card>
            <CardContent className="p-8 text-center text-slate-500">
              {searchTerm ? 'No matching results found' : 'No active PPM assignments found'}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Desktop Data Table */}
      <Card className="hidden md:block">
        <CardContent className="p-0">
          <DataTable 
            columns={columns} 
            data={filteredAssigned}
            isLoading={loading}
            emptyMessage="No active PPM assignments found"
          />
        </CardContent>
      </Card>

      {/* Run Scheduler Dialog */}
      <Dialog open={showRunScheduler} onOpenChange={setShowRunScheduler}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Run PPM Scheduler</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              This will check all active PPM assignments and create jobs for any that are due today or overdue.
            </p>
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="h-4 w-4" />
                <span className="font-medium">Important</span>
              </div>
              <p className="text-sm text-amber-600 mt-1">
                Duplicate jobs will not be created. Only new jobs for due items without existing open jobs.
              </p>
            </div>
            
            {schedulerResult && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="font-medium text-green-700">Scheduler Result:</div>
                <ul className="text-sm text-green-600 mt-2 space-y-1">
                  <li>✓ Created: {schedulerResult.jobs_created} jobs</li>
                  <li>○ Skipped: {schedulerResult.skipped} (duplicates or missing assets)</li>
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRunScheduler(false)}>Close</Button>
            <Button 
              onClick={runScheduler} 
              className="bg-[#1F7A78] hover:bg-[#16615f]"
              disabled={schedulerRunning}
              data-testid="confirm-run-scheduler"
            >
              {schedulerRunning ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <PlayCircle className="h-4 w-4 mr-2" />
                  Run Now
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
